import type { ProductCategoryProductCategoryReadModel } from '@r/api/generated/client.schemas.ts';
import { flattenCategories, getCategoryParentId } from './custom-shop-categories-api.ts';
import { canBeChildCategory, canBeParentCategory } from './custom-shop-categories-flow-rules.ts';

const NODE_WIDTH = 180;
const HORIZONTAL_GAP = 48;
const VERTICAL_GAP = 120;

export type CategoryFlowNodeData = {
  label: string;
  canBeParent: boolean;
  canBeChild: boolean;
  hasParent: boolean;
  hasChildren: boolean;
};

export type CategoryFlowNode = {
  id: string;
  type: 'category';
  position: { x: number; y: number };
  data: CategoryFlowNodeData;
};

export type CategoryFlowEdge = {
  id: string;
  source: string;
  target: string;
  sourceHandle?: string;
  targetHandle?: string;
  type: 'category';
};

function getParentId(category: ProductCategoryProductCategoryReadModel): number | null {
  return getCategoryParentId(category);
}

/** Indique si une catégorie possède au moins un enfant dans la liste aplatie. */
function categoryHasLinkedChildren(
  categoryId: number,
  categories: readonly ProductCategoryProductCategoryReadModel[]
): boolean {
  return flattenCategories(categories).some(
    (category) => getCategoryParentId(category) === categoryId
  );
}

/** Prépare les métadonnées d'affichage et de connexion d'un nœud React Flow. */
function toFlowNodeData(
  category: ProductCategoryProductCategoryReadModel,
  categories: readonly ProductCategoryProductCategoryReadModel[]
): CategoryFlowNodeData {
  const hasParent = getCategoryParentId(category) !== null;
  const hasChildren =
    (category.childs?.length ?? 0) > 0 || categoryHasLinkedChildren(category.id, categories);

  return {
    label: category.label,
    canBeParent: canBeParentCategory(category),
    canBeChild: canBeChildCategory(category, categories),
    hasParent,
    hasChildren
  };
}

/** Construit un nœud React Flow positionné pour une catégorie. */
function toFlowNode(
  category: ProductCategoryProductCategoryReadModel,
  position: { x: number; y: number },
  categories: readonly ProductCategoryProductCategoryReadModel[]
): CategoryFlowNode {
  return {
    id: String(category.id),
    type: 'category',
    position,
    data: toFlowNodeData(category, categories)
  };
}

/** Layout à deux niveaux : principales (y=0) puis sous-catégories (y=VERTICAL_GAP). */
function layoutTwoLevelTree(categories: readonly ProductCategoryProductCategoryReadModel[]): {
  nodes: CategoryFlowNode[];
  edges: CategoryFlowEdge[];
} {
  const roots = categories.filter((category) => getParentId(category) === null);
  const nodes: CategoryFlowNode[] = [];
  const edges: CategoryFlowEdge[] = [];
  let offsetX = 0;

  for (const root of roots) {
    const children = categories.filter((category) => getParentId(category) === root.id);
    const childrenWidth =
      children.length * NODE_WIDTH + Math.max(children.length - 1, 0) * HORIZONTAL_GAP;
    const ownWidth = Math.max(NODE_WIDTH, childrenWidth);

    nodes.push(toFlowNode(root, { x: offsetX + ownWidth / 2 - NODE_WIDTH / 2, y: 0 }, categories));

    const childStartX = offsetX + ownWidth / 2 - childrenWidth / 2;
    children.forEach((child, childIndex) => {
      nodes.push(
        toFlowNode(
          child,
          {
            x: childStartX + childIndex * (NODE_WIDTH + HORIZONTAL_GAP),
            y: VERTICAL_GAP
          },
          categories
        )
      );
      edges.push({
        id: `${root.id}-${child.id}`,
        source: String(root.id),
        sourceHandle: 'source',
        target: String(child.id),
        targetHandle: 'target',
        type: 'category'
      });
    });

    offsetX += ownWidth + HORIZONTAL_GAP;
  }

  return { nodes, edges };
}

/** Transforme les catégories API en nœuds et arêtes React Flow. */
export function categoriesToFlowElements(
  categories: readonly ProductCategoryProductCategoryReadModel[]
): { nodes: CategoryFlowNode[]; edges: CategoryFlowEdge[] } {
  const flatCategories = flattenCategories(categories);

  if (flatCategories.length === 0) {
    return { nodes: [], edges: [] };
  }

  const hasParentLinks = flatCategories.some((category) => getParentId(category) !== null);

  if (!hasParentLinks) {
    return {
      nodes: flatCategories.map((category, index) =>
        toFlowNode(category, { x: index * (NODE_WIDTH + HORIZONTAL_GAP), y: 0 }, flatCategories)
      ),
      edges: []
    };
  }

  return layoutTwoLevelTree(flatCategories);
}
